'use client';

import { variables } from './variables';
import { createTheme } from '@mui/material/styles';
import { createBreakpoints } from '@mui/system';
import sharedTheme from '../shared/theme';

import { SuccessIcon } from '@/assets/theme/m3gr/alertIcons/SuccessIcon';
import { WarningIcon } from '@/assets/theme/m3gr/alertIcons/WarningIcon';
import { ErrorIcon } from '@/assets/theme/m3gr/alertIcons/ErrorIcon';
import { InfoIcon } from '@/assets/theme/m3gr/alertIcons/InfoIcon';
import { addOpacityToThemeColor } from '@/utils/styles/addOpacity';

const { colors, fonts, borderRadius, padding, boxShadows, typography } = variables;

const stepperLineBackground = (color: string) =>
    `50% 50% no-repeat url("data:image/svg+xml,%3Csvg width='19' height='9' viewBox='0 0 19 9' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18.3536 4.85355C18.5488 4.65829 18.5488 4.34171 18.3536 4.14645L15.1716 0.964466C14.9763 0.769204 14.6597 0.769204 14.4645 0.964466C14.2692 1.15973 14.2692 1.47631 14.4645 1.67157L17.2929 4.5L14.4645 7.32843C14.2692 7.52369 14.2692 7.84027 14.4645 8.03553C14.6597 8.2308 14.9763 8.2308 15.1716 8.03553L18.3536 4.85355ZM0 5H18V4H0V5Z' fill='${encodeURIComponent(color)}'/%3E%3C/svg%3E");`;

const breakpoints = createBreakpoints({});

const defaultTheme = sharedTheme(variables);

const outlinedInputStyles = {
    styleOverrides: {
        root: {
            ...defaultTheme.MuiOutlinedInput.styleOverrides.root,
            borderRadius: borderRadius.primary,

            '.MuiOutlinedInput-notchedOutline, .MuiPickersOutlinedInput-notchedOutline': {
                borderColor: colors.palette.neutral['400'],
                transition: 'all 0.15s ease-in-out',
            },

            '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: colors.other.inputs.focus,
                    boxShadow: boxShadows.inputShadow,
                },
            },

            '&.Mui-disabled': {
                background: colors.palette.neutral['100'],

                '& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline': {
                    borderColor: 'transparent',
                },

                '&:hover .MuiOutlinedInput-notchedOutline, &:hover .MuiPickersOutlinedInput-notchedOutline':
                    {
                        borderColor: 'transparent',
                    },
            },
        },

        notchedOutline: {
            borderRadius: borderRadius.primary,
        },
    },
};

export const theme = createTheme({
    colors,
    fonts,
    borderRadius,
    padding,
    palette: colors.palette,
    typography,
    components: {
        MuiTypography: {
            styleOverrides: {
                root: {
                    lineHeight: '140%',
                    '&.homePageTitle': {
                        color: 'white',
                        fontSize: '2.625rem',
                        marginBottom: '24px',
                        b: {
                            display: 'inline-block',
                            fontWeight: '800',
                            lineHeight: '120%',
                            maxWidth: '347px',
                            '&::after': {
                                content: '""',
                                display: 'block',
                                width: '100%',
                                height: '8px',
                                backgroundImage: 'url("/images/themes/m3gr/underline.svg")',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'left',
                                marginTop: '8px',
                            },
                        },
                        ul: {
                            paddingInlineStart: '24px',
                            margin: '30px 0',
                        },
                        li: {
                            listStyle: 'none',
                            fontSize: '1.375rem',
                            fontWeight: '400',
                            fontFamily: fonts.secondary,
                            lineHeight: '150%',
                            position: 'relative',
                            paddingLeft: '24px',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '14px',
                                height: '10px',
                                background: 'url("/images/themes/m3gr/tick-icon.svg")',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                            },
                        },
                        [breakpoints.up('md')]: {
                            marginBottom: '60px',
                        },
                    },
                    '&.homePageDescription': {
                        marginTop: '0',
                        fontSize: '1em',
                        fontWeight: 400,
                        color: colors.palette.text.primary,
                    },
                    '&.nmDashboardWelcomeTitle': {
                        color: colors.palette.primary.main,
                        fontSize: '1rem',
                        textTransform: 'lowercase',
                        '&:first-letter': {
                            textTransform: 'uppercase',
                        },
                    },
                    '&.nmDashboardWelcomeText': {
                        fontSize: '1rem',
                        color: colors.palette.neutral['800'],
                    },
                    '&.nmDashboardJoinTitle': {
                        color: colors.palette.primary.main,
                    },
                    '&.nmDashboardNote': {
                        fontSize: '0.75rem',
                    },
                    '&.nmSurveyCardLabel': {
                        color: 'rgba(127, 136, 152, 1)',
                    },
                    '&.surveyCardProjectNumber': {
                        fontFamily: fonts.secondary,
                        fontSize: '0.875rem',
                        fontWeight: '400',
                    },
                    '&.surveyCardTitle': {
                        color: colors.palette.text.primary,
                        fontWeight: '800',
                    },
                    '&.surveyDetailsTitle': {
                        color: colors.palette.neutral['500'],
                    },
                    '&.surveyDetailsValue': {
                        color: colors.palette.primary.main,
                        fontFamily: fonts.secondary,
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        textTransform: 'none',
                        marginRight: '16px',
                        '.currency': {
                            color: colors.palette.secondary.main,
                        },
                    },
                    '&.dynamicStaticPagesLink': {
                        color: colors.palette.primary.main,
                        fontWeight: 700,
                        textDecoration: 'underline',
                    },
                    '&.badgeDefinitionTitle, &.introTitle': {
                        fontSize: '1rem',
                    },
                    '&.badgeDefinitionTitle': {
                        margin: '0 0 8px 0',
                    },
                    '&.badgeDefinitionDescription, &.introDescription': {
                        fontSize: '1rem',
                        fontWeight: '400',
                        fontFamily: fonts.secondary,
                        color: colors.palette.neutral['700'],
                    },
                    '&.introDescription': {
                        br: {
                            display: 'block',
                            content: '""',
                            marginTop: '8px',
                        },
                        a: {
                            color: colors.palette.tertiary.main,
                            '&:hover': {
                                color: colors.palette.tertiary['300'],
                            },
                            '&:active': {
                                color: colors.palette.tertiary['700'],
                            },
                        },
                    },
                    '&.ProfileQuestionTitle': {
                        color: colors.palette.text.primary,
                        fontFamily: fonts.secondary,
                        fontSize: '1rem',
                        fontWeight: '600',
                    },
                    '&.privacyDescription': {
                        color: colors.palette.text.primary,
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                ...defaultTheme.MuiCssBaseline.styleOverrides,
                'b, strong': {
                    fontWeight: '600',
                },
                'h1, h2, h3, h4, h5, h6, h7': {
                    color: colors.palette.primary.main,
                    fontWeight: '800',
                },
                'h5, h6, h7': {
                    color: colors.palette.text.primary,
                },
                '.topMenuLinksList': {
                    position: 'relative',
                    left: '0',
                    transform: 'translateX(0)',
                    a: {
                        textDecoration: 'none',
                    },
                },
                '&.triangleArrowUp': {
                    borderBottom: `5px solid ${colors.other.text.header}`,
                },
                '&.triangleArrowDown': {
                    borderTop: `5px solid ${colors.other.text.header}`,
                },
                '&.verificationFormElementBlock ': {
                    paddingRight: '8px',
                    paddingLeft: '8px',
                },
                '&.verificationSubmitButtonWrapper': {
                    paddingRight: '8px',
                },
                '&.paymentMethodComingSoonLabel': {
                    color: colors.other.text.info,
                    background: 'rgb(229, 246, 253)',
                    padding: '2px 6px',
                    top: '0',
                    right: '0',
                },
                '.referralActionButtonBlock': {
                    padding: '16px 24px 24px 24px',
                },
                '.referralDetailsId': {
                    backgroundColor: 'rgba(252,252,252,1)',
                    boxShadow: '0 1px 0px rgba(232,232,232,1)',
                },
                '.referralsDetailTitle': {
                    color: colors.palette.primary.main,
                },
                '.referralsFiltersWrapper': {
                    padding: '24px',
                },
                '.privacyterms': {
                    fontSize: '17px',
                    fontWeight: '600',
                    paddingBottom: '10px',
                },
                '&.WalletMinMaxLabel': {
                    backgroundColor: colors.other.background.highlight,
                    color: colors.palette.primary.main,
                    borderRadius: borderRadius.primary,
                },
                '.HeaderLogoNewYearAnimation': {
                    filter: 'brightness(2)',
                },
                '.AccountUserIdBlock ': {
                    backgroundColor: 'rgba(25, 118, 210, 0.02)',
                    borderRadius: borderRadius.primary,
                },
                '.AccountUserId': {
                    fontSize: '16px',
                    fontWeight: '600',
                    color: colors.palette.text.primary,
                },
                '&.language-info': {
                    marginRight: '0.5rem',
                    '.SelectedLocale': {
                        fontSize: '1rem',
                        fontWeight: '400',
                    },
                    [breakpoints.up('md')]: {
                        marginRight: '0',
                    },
                },
                '&.topmenuButtonIcon': {
                    color: 'white',
                },
                '&.Header': {
                    '.headerWrapper': {
                        paddingTop: '1px',
                        paddingBottom: '1px',
                    },
                    '.HeaderPanelImageLogo': {
                        width: '51px',
                        height: '52px',
                    },
                    '.HeaderLogo, .HeaderLogo a': {
                        width: '100%',
                        height: '100%',
                    },
                    '.HeaderLogo a': {
                        display: 'inline-flex',
                        background:
                            'url("/images/themes/m3gr/logo-header-mobile.svg") no-repeat 0 0',
                    },
                    '.HeaderLogo img': {
                        width: '100%',
                        height: 'auto',
                        display: 'none',
                    },
                    '.mobileMenuBox': {
                        boxShadow: boxShadows.headerShadow,
                    },
                },
                '&.Footer': {
                    '.followUsTitle': {
                        color: colors.palette.neutral[700],
                    },
                    '.MuiStack-root': {
                        alignItems: 'flex-start',
                    },
                    '.footerMenu': {
                        marginTop: '16px',
                        li: {
                            textAlign: 'left',
                            marginBottom: '8px',
                        },
                    },
                    '.copyright': {
                        textAlign: 'left',
                    },
                    '.FooterLogo': {
                        paddingTop: '16px',
                        paddingBottom: '16px',
                        'a img': {
                            width: '265px',
                            height: '32px',
                        },
                    },
                },
                [breakpoints.up('md')]: {
                    '&.Footer': {
                        '.MuiStack-root': {
                            alignItems: 'flex-start',
                        },
                    },
                },
                [breakpoints.up('lg')]: {
                    '&.Footer': {
                        '.FooterLogo': {
                            paddingTop: '0',
                        },
                        '.footerMenu': {
                            marginTop: '0',
                        },
                    },
                },
                [breakpoints.up('xl')]: {
                    '&.Header': {
                        '.HeaderPanelImageLogo': {
                            width: '290px',
                            height: '34px',
                        },
                        '.HeaderLogo a': {
                            background: 'none',
                        },
                        '.HeaderLogo img': {
                            display: 'block',
                        },
                    },
                },
                '&.introRegistrationWrapper': {
                    position: 'relative',
                    backgroundImage: 'url("/images/themes/m3gr/top-banner.jpg")',
                    backgroundPosition: 'center top',
                    backgroundRepeat: 'no-repeat',
                    [breakpoints.up('sm')]: {
                        backgroundSize: '1200px auto',
                    },
                    [breakpoints.up('md')]: {
                        backgroundSize: '1536px auto',
                    },
                    '.registrationContainer': {
                        width: '100%',
                    },
                    '.introTextBlock': {
                        display: 'flex',
                        flexDirection: 'column',
                        marginBottom: '24px',
                        [breakpoints.up('lg')]: {
                            width: '364px',
                            flexShrink: '0',
                            marginRight: '30px',
                            marginBottom: '0',
                        },
                        '.homePageDescription': {
                            height: '100%',
                            flexGrow: '1',
                            background: colors.palette.neutral['50'],
                            padding: '40px 32px',
                            borderRadius: borderRadius.primary,
                        },
                    },
                },
                '&.ForgotPasswordLink': {
                    fontSize: '0.875rem',
                    fontWeight: '500',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiPaper.styleOverrides.root,
                    border: `1px solid ${colors.palette.neutral['100']}`,
                    boxShadow: 'none',
                    '&.lang-switcher-wrapper': {
                        maxHeight: 'calc(100% - 32px)',
                    },
                    '&.warningBox': {
                        color: colors.palette.neutral['800'],
                        border: `1px solid ${colors.other.text.warning}`,
                        h3: {
                            fontFamily: fonts.primary,
                            color: colors.palette.primary['800'],
                            fontWeight: 800,
                            fontSize: '1rem',
                            paddingTop: '3px',
                        },
                        svg: {
                            width: '0.8em',
                            height: '0.8em',
                        },
                    },
                    '&.introPicBlock': {
                        display: 'inline-flex',
                        flexShrink: '1',
                        overflow: 'hidden',
                        padding: 0,
                        lineHeight: 0,
                        border: '0',
                        borderRadius: borderRadius.primary,
                        background: 'white',
                        width: '100%',
                        boxShadow: 'none',
                        marginTop: '15px',
                        maxHeight: '464px',
                        order: '2',
                        img: {
                            display: 'block',
                            width: '100%',
                            height: 'auto',
                            minHeight: 'fit-content',
                            objectFit: 'contain',
                        },
                        [breakpoints.up('lg')]: {
                            maxHeight: '232px',
                        },
                    },
                    '&.accountTaxDetails': {
                        '.helperText': {
                            position: 'relative',
                            backgroundColor: colors.palette.background.default,
                            padding: '16px',
                            [breakpoints.up('md')]: {
                                marginLeft: '20px',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '15px',
                                    left: '-9px',
                                    width: '0',
                                    height: '0',
                                    borderTop: '10px solid transparent',
                                    borderBottom: '10px solid transparent',
                                    borderRight: `10px solid ${colors.palette.background.default}`,
                                    transform: 'translateY(-50%)',
                                },
                            },
                        },
                    },
                    '&.infoBox': {
                        border: 'none',
                        backgroundColor: 'transparent',
                        paddingBottom: '30px',
                        margin: '0 -12px',
                        [breakpoints.up('lg')]: {
                            marginRight: '-24px',
                        },
                        '.introTitle': {
                            maxWidth: '100%',
                            margin: '12px auto',
                            minHeight: '0',
                        },
                        '.homeIntroIconsItem': {
                            border: `1px solid ${colors.palette.neutral['100']}`,
                            borderRadius: borderRadius.primary,
                            '.introIcon': {
                                width: '40px',
                                height: '40px',
                            },
                        },
                    },
                    '&.infoCard': {
                        boxShadow: 'none',

                        '.infoCardRow': {
                            justifyContent: 'space-between',
                            flexWrap: 'nowrap',
                            columnGap: '24px',
                        },
                        '.infoCardItem': {
                            border: `1px solid ${colors.palette.neutral[100]}`,
                            background: 'white',
                            flexBasis: '100%',
                            maxWidth: '100%',
                            marginTop: '24px',
                            borderRadius: borderRadius.primary,
                        },
                        [breakpoints.up('sm')]: {
                            '.infoCardItem': {
                                flexBasis: 'calc(50% - 12px)',
                                maxWidth: 'calc(50% - 12px)',
                            },
                        },
                        [breakpoints.up('lg')]: {
                            '.infoCardItem': {
                                marginTop: 0,
                                flexBasis: 'calc(33.3% - 15px)',
                                maxWidth: 'calc(33.3% - 15px)',
                            },
                        },
                    },
                    '&.accountMenuWrapper': {
                        border: 'none',
                    },
                    '&.dynamicStaticPagesItem': {
                        transition: 'all 0.15s',
                    },
                    '&.dynamicStaticPagesItem:hover': {
                        borderColor: colors.palette.primary.main,
                    },
                    '.ProfileQuestionsContent': {
                        border: `1px solid ${colors.palette.background.default}`,
                    },
                    '&.badgeDefinitions': {
                        display: 'grid',
                        border: 'none',
                        gap: '0',
                        '.badgeDefinitionRow': {
                            display: 'grid',
                            gap: '0',
                            gridAutoFlow: 'column',
                            gridAutoColumns: '1fr',
                            overflowX: 'auto',
                            '.badgeDefinition': {
                                border: `1px solid ${colors.palette.neutral['100']}`,
                                borderRadius: borderRadius.primary,
                                width: '100%',
                            },
                        },
                    },
                    [breakpoints.up('lg')]: {
                        '&.badgeDefinitions': {
                            gap: '24px',
                        },
                    },
                },
                elevation1: {
                    '.MuiTypography-h4': {
                        color: colors.palette.primary.main,
                    },
                },
            },
        },
        MuiPopover: defaultTheme.MuiPopover,
        MuiOutlinedInput: outlinedInputStyles,
        MuiPickersOutlinedInput: outlinedInputStyles,
        MuiChip: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiChip.styleOverrides.root,
                    fontWeight: 400,
                },
            },
        },
        MuiAutocomplete: defaultTheme.MuiAutocomplete,
        MuiSelect: defaultTheme.MuiSelect,
        MuiAccordion: defaultTheme.MuiAccordion,
        MuiList: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiList.styleOverrides.root,
                    '&.MuiMenu-list': {
                        border: 'none',
                    },
                    '& a > li': {
                        color: 'white',
                        '&:hover': {
                            color: 'white',
                            backgroundColor: 'rgba(255, 255, 255, 0.10)',
                        },
                    },
                    '& a .active': {
                        color: colors.palette.primary.main,
                        backgroundColor: 'white',
                    },
                    '&.topNavigationMenu': {
                        border: 'none',
                        marginTop: '-12px',
                        li: {
                            color: colors.palette.primary.main,
                            padding: '8px 16px',
                            marginTop: '8px',
                            width: '100%',
                            '&:hover': {
                                color: colors.palette.primary['300'],
                            },
                        },
                        a: {
                            textDecoration: 'none',
                            width: '100%',
                            padding: '0 16px',
                        },
                        'li:not(.active):hover': {
                            background: 'transparent',
                        },
                        '.active': {
                            color: colors.palette.primary.main,
                            backgroundColor: addOpacityToThemeColor(
                                colors.palette.primary.main,
                                0.05,
                            ),
                            '&:hover': {
                                color: colors.palette.primary.main,
                                backgroundColor: addOpacityToThemeColor(
                                    colors.palette.primary.main,
                                    0.1,
                                ),
                            },
                        },
                    },
                    '&.topNavigationMenu .selectOpinionTopMenuItem': {
                        display: 'flex',
                        gap: '0.75rem',
                        color: 'white',
                        borderRadius: borderRadius.primary,
                        backgroundColor: 'rgba(29, 81, 86, 1)',
                    },
                },
            },
        },
        MuiMenu: defaultTheme.MuiMenu,
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiMenuItem.styleOverrides.root,
                    '& a': {
                        fontWeight: 400,
                        color: colors.palette.text.primary,
                    },
                    '&.active': {
                        '& a': {
                            color: 'white',
                            background: colors.palette.primary.main,
                            fontWeight: 600,
                        },
                    },
                    '&:not(.active):hover': {
                        backgroundColor: 'rgba(243, 249, 255, 1)',
                        '& a': {
                            color: colors.palette.primary.main,
                            backgroundColor: 'white',
                        },
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'white',
                        color: colors.palette.primary.main,
                    },
                    '&.accountMenuItem': {
                        display: 'block',
                        padding: '0',
                        marginBottom: '4px',
                        borderRadius: borderRadius.primary,
                        background: 'transparent',
                        a: {
                            display: 'block',
                            fontWeight: '400',
                            color: colors.palette.text.primary,
                            padding: '9px 16px',
                            borderRadius: borderRadius.primary,
                            background: 'rgba(249, 249, 249, 1)',
                            transition: 'all 0.15s',
                            '&:hover': {
                                color: colors.palette.primary['300'],
                                background: 'rgba(249, 249, 249, 1)',
                            },
                        },
                        '&.active a': {
                            color: 'white',
                            background: 'rgba(0, 47, 99, 1)',
                            '&:hover': {
                                color: 'white',
                                background: colors.palette.primary['400'],
                            },
                        },
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginBottom: '10px',
                    '&.radioGroupThemeControl': {
                        borderRadius: borderRadius.primary,
                        backgroundColor: 'transparent',
                        border: `1px solid ${colors.palette.neutral['100']}`,
                        '&.radioGroupControlActive': {
                            borderColor: colors.palette.secondary['100'],
                            background: 'white',
                        },
                        '&:hover': {
                            borderRadius: borderRadius.primary,
                            borderColor: colors.palette.secondary['100'],
                            background: addOpacityToThemeColor(
                                colors.palette.secondary['100'],
                                0.2,
                            ),
                        },
                        '.MuiFormControlLabel-root': {
                            borderRadius: borderRadius.primary,
                            '&:hover': {
                                background: 'transparent',
                                boxShadow: 'none',
                            },
                            '&.Mui-disabled, &.Mui-disabled span': {
                                cursor: 'default',
                            },
                        },
                        '.ProfileQuestionsContent &:has(:not([value=""]):not(:focus):invalid) .MuiInputLabel-root':
                            {
                                color: colors.other.text.error,
                            },
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiInputLabel.styleOverrides.root,
                    color: colors.palette.neutral['500'],
                    '&.Mui-focused, &.Mui-error': {
                        color: colors.palette.neutral['500'],
                    },
                    '&.MuiInputLabel-shrink': {
                        transform: 'translate(0, -18px) scale(0.75)',
                    },
                    '&.MuiInputLabel-shrink + .MuiInputBase-root:not(.Mui-focused):not(.Mui-disabled):not(:hover) .MuiOutlinedInput-notchedOutline':
                        {
                            borderColor: colors.palette.neutral['600'],
                        },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontFamily: fonts.secondary,
                },
            },
        },
        MuiFormControlLabel: defaultTheme.MuiFormControlLabel,
        MuiCheckbox: {
            styleOverrides: {
                ...defaultTheme.MuiCheckbox.styleOverrides,
                root: {
                    ...defaultTheme.MuiCheckbox.styleOverrides.root,
                    '&.Mui-disabled': {
                        color: colors.palette.neutral['500'],
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                ...defaultTheme.MuiCheckbox.styleOverrides,
                root: {
                    ...defaultTheme.MuiRadio.styleOverrides.root,
                    '&.Mui-disabled': {
                        color: colors.palette.neutral['500'],
                    },
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    '.MuiSwitch-thumb': {
                        backgroundColor: 'white',
                        filter: 'drop-shadow(0px 2px 1px rgba(119, 119, 119, 0.20))',
                    },
                    '.MuiSwitch-switchBase + .MuiSwitch-track': {
                        backgroundColor: colors.palette.neutral['500'],
                        opacity: 0.38,
                    },
                    '.Mui-checked .MuiSwitch-thumb': {
                        backgroundColor: colors.palette.primary['500'],
                    },
                    '.MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: colors.palette.primary['500'],
                        opacity: 0.5,
                    },
                    '.Mui-disabled .MuiSwitch-thumb': {
                        backgroundColor: colors.palette.neutral['100'],
                    },
                    '.MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                        backgroundColor: colors.palette.neutral['300'],
                        opacity: 1,
                    },
                },
            },
        },
        MuiTouchRipple: defaultTheme.MuiTouchRipple,
        MuiFormHelperText: defaultTheme.MuiFormHelperText,
        MuiAlert: {
            defaultProps: {
                iconMapping: {
                    success: SuccessIcon(),
                    warning: WarningIcon(),
                    error: ErrorIcon(),
                    info: InfoIcon(),
                },
            },
            styleOverrides: {
                message: {
                    ...defaultTheme.MuiAlert.styleOverrides.message,
                    color: colors.palette.neutral['700'],
                    fontWeight: 400,
                    fontSize: '0.875rem',
                },
                outlinedInfo: {
                    borderColor: colors.other.text.info,
                },
                outlinedSuccess: {
                    borderColor: colors.other.text.success,
                },
                outlinedError: {
                    borderColor: colors.other.text.error,
                },
                outlinedWarning: {
                    borderColor: colors.other.text.warning,
                },
                standardInfo: {
                    backgroundColor: colors.other.background.info,
                },
                filledInfo: {
                    color: colors.other.text.info,
                    backgroundColor: colors.other.background.info,
                },
                standardSuccess: {
                    backgroundColor: colors.other.background.success,
                },
                filledSuccess: {
                    color: colors.other.text.success,
                    backgroundColor: colors.other.background.success,
                },
                standardError: {
                    backgroundColor: colors.other.background.error,
                },
                filledError: {
                    color: colors.other.text.error,
                    backgroundColor: colors.other.background.error,
                },
                standardWarning: {
                    backgroundColor: colors.other.background.warning,
                },
                filledWarning: {
                    color: colors.other.text.warning,
                    backgroundColor: colors.other.background.warning,
                },
                root: {
                    '&.notificationAlert': {
                        border: '0',
                        position: 'relative',
                    },
                    '&.notificationAlertWarning': {
                        backgroundColor: colors.other.background.warning,
                        '.MuiAlert-message': {
                            color: colors.other.text.warning,
                        },
                    },
                    '&.notificationAlertError': {
                        backgroundColor: colors.other.background.error,
                        '.MuiAlert-message': {
                            color: colors.other.text.error,
                        },
                    },
                    '&.notificationAlertInfo': {
                        backgroundColor: colors.other.background.info,
                        '.MuiAlert-message': {
                            color: colors.other.text.info,
                        },
                    },
                    '&.notificationAlertSuccess': {
                        backgroundColor: colors.other.background.success,
                        '.MuiAlert-message': {
                            color: colors.other.text.success,
                        },
                    },
                },
            },
        },
        MuiButton: {
            variants: [
                {
                    props: { variant: 'tertiary' },
                    style: {
                        height: '34px',
                        color: 'white',
                        fontSize: '1rem',
                        fontFamily: fonts.secondary,
                        padding: '4px 16px',
                        borderRadius: borderRadius.primary,
                        '&:hover, &.active:hover': {
                            color: 'white',
                            backgroundColor: 'rgba(255, 255, 255, 0.10)',
                        },
                        '&.focus:hover': {
                            color: 'white',
                            backgroundColor: 'rgba(255, 255, 255, 0.10)',
                        },
                    },
                },
                {
                    props: { variant: 'descriptive' },
                    style: {
                        padding: '16px',
                        color: colors.palette.text.primary,
                        backgroundColor: 'white',
                        border: `1px solid ${colors.palette.neutral['100']}`,
                        transition: 'color 0.15s',
                        '.description': {
                            fontSize: '1rem',
                            color: colors.palette.text.primary,
                        },
                        h6: {
                            fontSize: '1rem',
                            color: colors.palette.text.primary,
                        },
                        '&:hover': {
                            color: 'white',
                            backgroundColor: colors.palette.secondary['500'],
                            border: `1px solid ${colors.palette.secondary['500']}`,
                            '.description': {
                                color: 'white',
                            },
                            h6: {
                                color: 'white',
                            },
                        },
                    },
                },
            ],
            styleOverrides: {
                root: {
                    fontFamily: fonts.secondary,
                    fontSize: '1rem',
                    fontWeight: '400',
                    lineHeight: '140%',
                    textTransform: 'none',
                    boxShadow: 'none',
                    padding: '8px 24px',
                    '&:hover, &:focus, &:active': {
                        boxShadow: 'none',
                    },
                    '&:disabled': {
                        backgroundColor: colors.palette.neutral['100'],
                        borderColor: colors.palette.neutral['100'],
                        '&:not(.MuiButton-loading)': {
                            color: colors.palette.neutral['600'],
                        },
                    },
                    '&.nmSurveyCardButton': {
                        backgroundColor: colors.palette.primary.main,
                        color: 'white',
                        fontSize: '1rem',
                    },
                    '&.language-button': {
                        fontSize: '1rem',
                        fontWeight: '400',
                        marginRight: '1rem',
                        height: '38px',
                        color: colors.palette.primary.main,
                        '&.mobileLocaleButton': {
                            backgroundColor: 'transparent',
                            height: '36px',
                        },
                        '&:hover': {
                            color: colors.palette.primary['300'],
                            backgroundColor: addOpacityToThemeColor(
                                colors.palette.primary.main,
                                0.05,
                            ),
                        },
                        '&:focus, &:active, &[aria-expanded="true"]': {
                            color: colors.palette.primary.main,
                            backgroundColor: addOpacityToThemeColor(
                                colors.palette.primary.main,
                                0.05,
                            ),
                            border: 'none',
                        },
                        [breakpoints.up('md')]: {
                            marginRight: '0.5rem',
                        },
                    },
                    '&.topMenuButton': {
                        color: colors.palette.primary.main,
                        margin: '0 4px',
                        padding: '12px 16px',
                        height: '38px',
                        fontWeight: '400',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                            color: colors.palette.primary['300'],
                        },
                        '&.active': {
                            backgroundColor: addOpacityToThemeColor(
                                colors.palette.primary.main,
                                0.05,
                            ),
                            color: colors.palette.primary.main,
                            '&:hover': {
                                color: colors.palette.primary.main,
                                backgroundColor: addOpacityToThemeColor(
                                    colors.palette.primary.main,
                                    0.1,
                                ),
                            },
                        },
                        [breakpoints.up('lg')]: {
                            padding: '12px 24px',
                            margin: '0 8px',
                        },
                        [breakpoints.up('lg')]: {
                            padding: '12px 24px',
                            margin: '0 8px',
                        },
                        '&.topMenuButton.selectOpinionTopMenuItem': {
                            display: 'flex',
                            gap: '0.75rem',
                            color: 'white',
                            backgroundColor: 'rgba(29, 81, 86, 1)',
                            ':hover': {
                                backgroundColor: 'rgba(74, 116, 120, 1)',
                            },
                        },
                    },
                    '&.surveyCardButton, &.dynamicProfilingSubmit': {
                        color: colors.palette.primary.main,
                        border: `1px solid ${colors.palette.primary.main}`,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                            color: colors.palette.primary['300'],
                            border: `1px solid ${colors.palette.primary['300']}`,
                            backgroundColor: 'white',
                        },
                        '&:active, &:focus': {
                            color: colors.palette.primary['700'],
                            border: `1px solid ${colors.palette.primary['700']}`,
                            backgroundColor: 'white',
                        },
                    },
                    '&.surveyLoadMoreButton': {
                        width: '100%',
                        backgroundColor: 'transparent',
                        color: colors.palette.secondary['500'],
                        border: `1px solid ${colors.palette.secondary['500']}`,
                        '&:hover': {
                            backgroundColor: 'transparent',
                            color: colors.palette.secondary['300'],
                            border: `1px solid ${colors.palette.secondary['300']}`,
                        },
                        '&:focus': {
                            backgroundColor: 'transparent',
                            color: colors.palette.secondary['700'],
                            border: `1px solid ${colors.palette.secondary['700']}`,
                        },
                    },
                    '&.controlYourDataButton': {
                        color: colors.palette.primary['500'],
                        fontSize: '1rem',
                        '&:hover': {
                            color: colors.palette.primary['300'],
                        },
                        '&:active': {
                            color: colors.palette.primary['700'],
                        },
                    },
                },
                containedPrimary: {
                    backgroundColor: colors.palette.primary.main,
                    '&:hover': {
                        backgroundColor: colors.palette.primary['300'],
                    },
                    '&:active': {
                        backgroundColor: colors.palette.primary['700'],
                    },
                },
                outlined: {
                    color: colors.palette.primary.main,
                    border: `1px solid ${colors.palette.primary.main}`,
                    backgroundColor: 'white',
                    '&:hover': {
                        color: colors.palette.primary['300'],
                        border: `1px solid ${colors.palette.primary['300']}`,
                        backgroundColor: 'white',
                    },
                    '&:active, &:focus': {
                        color: colors.palette.primary['700'],
                        border: `1px solid ${colors.palette.primary['700']}`,
                        backgroundColor: 'white',
                    },
                    '&.loginButton, &.mobileLoginButton, &.logoutButton, &.mobileLogoutButton': {
                        color: 'white',
                        fontSize: '1rem',
                        fontWeight: '400',
                        padding: '12px 12px 12px 8px',
                        height: '38px',
                        backgroundColor: colors.palette.primary.main,
                        '&:hover': {
                            backgroundColor: colors.palette.primary['300'],
                        },
                        '&:focus, &:active': {
                            backgroundColor: colors.palette.primary['700'],
                        },
                    },
                    '&.mobileLoginButton, &.mobileLogoutButton': {
                        textIndent: '-99999rem',
                        padding: '8px 4px',
                        height: '36px',
                        minWidth: '0',
                        borderColor: 'transparent',
                        background: 'transparent',
                        '.topmenuButtonIcon': {
                            color: colors.palette.primary['500'],
                            transform: 'scale(1.25)',
                            margin: '0',
                        },
                        '&:hover': {
                            color: colors.palette.primary.main,
                            backgroundColor: 'rgba(0, 51, 102, 0.04)',
                            '.menuIcon div, .menuIcon:before, .menuIcon:after': {
                                background: colors.palette.primary.main,
                            },
                        },
                        '&:focus, &:active': {
                            color: colors.palette.primary.main,
                            backgroundColor: 'transparent',
                        },
                    },
                    '&.showMoreButton': {
                        borderColor: colors.palette.secondary.main,
                        color: colors.palette.secondary.main,
                    },
                },
            },
        },
        MuiBackdrop: defaultTheme.MuiBackdrop,
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '&.mobileLocaleButton': {
                        color: colors.palette.primary.main,
                        padding: 0,
                        marginRight: '1rem',
                    },
                    '&.mobileCloseMenuButton': {
                        width: '36px',
                        height: '36px',
                        padding: '4px 6px',
                        borderRadius: borderRadius.primary,
                        '.menuIcon div, .menuIcon:before, .menuIcon:after': {
                            background: colors.palette.primary.main,
                        },
                        '.MuiTouchRipple-root *': {
                            borderRadius: borderRadius.primary,
                        },
                    },
                    '&.mobileMenuButton': {
                        display: 'flex',
                        justifyContent: 'center',
                        algignItems: 'center',
                        width: '36px',
                        height: '36px',
                        color: 'white',
                        fontSize: '0.875rem',
                        padding: '4px 6px',
                        borderRadius: borderRadius.primary,
                        '.MuiTouchRipple-root *': {
                            borderRadius: borderRadius.primary,
                        },
                        '.menuIcon': {
                            width: '20px',
                            color: 'white',
                            padding: '0',
                        },
                        '.menuIcon div, .menuIcon:before, .menuIcon:after': {
                            background: colors.palette.primary.main,
                            height: '2px',
                            borderRadius: '2px',
                            margin: '5px 0',
                        },
                        '.menuIcon:before': {
                            marginTop: '0',
                        },
                        '.menuIcon:after': {
                            marginBottom: '0',
                        },
                        '&:hover': {
                            color: colors.palette.primary.main,
                            backgroundColor: 'rgba(0, 51, 102, 0.04)',
                            '.menuIcon div, .menuIcon:before, .menuIcon:after': {
                                background: colors.palette.primary.main,
                            },
                        },
                        '&:focus, &:active': {
                            color: colors.palette.primary.main,
                            backgroundColor: 'transparent',
                        },
                    },
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                root: {
                    '&.notificationBadge > .MuiBadge-badge': {
                        right: -10,
                        top: 5,
                        boxShadow: 'none',
                    },
                    '.accountMenuItem & > .MuiBadge-badge': {
                        top: 0,
                    },
                },
                badge: {
                    boxShadow: '0 2px 1px rgba(48, 48, 48, 0.32)',
                },
            },
        },
        MuiTooltip: defaultTheme.MuiTooltip,
        MuiStep: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStep.styleOverrides.root,
                    color: colors.palette.secondary['500'],
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    '& .Mui-active': {
                        color: colors.palette.secondary['500'],
                    },
                },
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStepIcon.styleOverrides.root,
                    borderRadius: borderRadius.primary,
                    color: 'transparent',
                    width: '38px',
                    height: '38px',
                    backgroundColor: colors.palette.neutral['100'],
                    path: {
                        display: 'none',
                    },
                    '&.Mui-active': {
                        color: colors.palette.secondary['500'],
                        background: colors.palette.secondary['500'],
                        '> text': {
                            fill: 'white',
                        },
                    },
                    '&.Mui-completed': {
                        color: colors.palette.secondary['300'],
                        background: `${colors.palette.secondary['300']} 50% 50% no-repeat url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18.7809 7.21876C19.0746 7.51251 19.0746 7.98751 18.7809 8.27813L10.5309 16.5313C10.2371 16.825 9.76211 16.825 9.47148 16.5313L5.21836 12.2813C4.92461 11.9875 4.92461 11.5125 5.21836 11.2219C5.51211 10.9313 5.98711 10.9281 6.27773 11.2219L9.99648 14.9406L17.7184 7.21876C18.0121 6.92501 18.4871 6.92501 18.7777 7.21876H18.7809Z' fill='white'/%3E%3C/svg%3E")`,
                        '> text': {
                            fill: 'white',
                        },
                    },
                },
                text: {
                    ...defaultTheme.MuiStepIcon.styleOverrides.text,
                    fill: colors.palette.neutral['500'],
                    fontFamily: fonts.primary,
                    fontSize: '0.66rem', // 16px, the parent font size is 24px
                    fontWeight: '800',
                    '&.Mui-active': {
                        fill: 'white',
                    },
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                root: defaultTheme.MuiStepLabel.styleOverrides.root,
                vertical: {
                    '.MuiStepLabel-label': {
                        ...defaultTheme.MuiStepLabel.styleOverrides.vertical['.MuiStepLabel-label'],
                        fontFamily: fonts.primary,
                        fontSize: '1rem',
                        fontWeight: '800',
                        borderRadius: borderRadius.primary,
                        '&.Mui-active': {
                            ...defaultTheme.MuiStepLabel.styleOverrides.vertical[
                                '.MuiStepLabel-label'
                            ]['&.Mui-active'],
                            fontWeight: '800',
                            color: colors.palette.secondary['500'],
                        },
                        '&.Mui-completed': {
                            fontWeight: '800',
                            color: colors.palette.secondary['500'],
                        },
                    },
                },
                horizontal: {
                    '.MuiStepLabel-label': {
                        ...defaultTheme.MuiStepLabel.styleOverrides.horizontal[
                            '.MuiStepLabel-label'
                        ],
                        borderRadius: borderRadius.primary,

                        '&.Mui-active': {
                            ...defaultTheme.MuiStepLabel.styleOverrides.horizontal[
                                '.MuiStepLabel-label'
                            ]['&.Mui-active'],
                            fontWeight: '800',
                            color: colors.palette.secondary['500'],
                        },
                        [breakpoints.up('sm')]: {
                            '&.Mui-active': {
                                marginLeft: '15px',
                                display: 'inline-block',
                            },
                        },
                        '&.Mui-completed': {
                            fontWeight: '800',
                            color: colors.palette.secondary['500'],
                        },
                    },
                },
            },
        },
        MuiStepConnector: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiStepConnector.styleOverrides.root,
                    '&.MuiStepConnector-horizontal .MuiStepConnector-line': {
                        borderColor: colors.palette.neutral['200'],
                        margin: '0 5px',
                    },
                    '&.MuiStepConnector-vertical .MuiStepConnector-line': {
                        borderColor: colors.palette.neutral['100'],
                        margin: '0 0 0 5px',
                    },
                    '&.Mui-active, &.Mui-completed': {
                        '.MuiStepConnector-line': {
                            borderColor: colors.palette.secondary['300'],
                        },
                    },
                    '&.Mui-active + .MuiStep-root + .MuiStepConnector-root': {
                        '.MuiStepConnector-line': {
                            borderColor: colors.palette.secondary['500'],
                        },
                    },
                    [breakpoints.up('sm')]: {
                        '&.MuiStepConnector-horizontal .MuiStepConnector-line': {
                            border: '0',
                            height: '10px',
                            background: stepperLineBackground(colors.palette.neutral['200']),
                        },
                        '&.Mui-active, &.Mui-completed': {
                            '&.MuiStepConnector-horizontal .MuiStepConnector-line': {
                                background: stepperLineBackground(colors.palette.secondary['300']),
                            },
                        },
                        '&.Mui-active + .MuiStep-root + .MuiStepConnector-horizontal': {
                            '.MuiStepConnector-line': {
                                background: stepperLineBackground(colors.palette.secondary['500']),
                            },
                        },
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    '&.surveyCard': {
                        border: `1px solid ${colors.palette.neutral['100']}`,
                        boxShadow: 'none',
                        transition: 'border 0.15s',
                        '&:hover': {
                            borderColor: colors.palette.neutral['300'],
                        },
                    },
                    '&.introRegistration': {
                        position: 'relative',
                        zIndex: '1',
                        width: '100%',
                        background: 'none',
                        border: 'none',
                        borderRadius: borderRadius.primary,
                        boxShadow: '0',
                        paddingTop: '60px',
                        '&:not(:has(.registrationBox))': {
                            '.MuiAlert-root': {
                                backgroundColor: addOpacityToThemeColor(
                                    colors.palette.primary['700'],
                                    0.6,
                                ),
                                border: 'none',
                                '.MuiAlert-message': {
                                    color: 'white',
                                },
                            },
                        },
                        [breakpoints.up('md')]: {
                            paddingTop: '90px',
                        },
                    },
                    '.referralDetails': {
                        padding: '10px 24px',
                    },
                    '.nmSurveyCardTitle': {
                        background: colors.palette.neutral['50'],
                        color: colors.palette.neutral['800'],
                    },
                    '.nmSurveyCardBaseInfo': {
                        background: colors.palette.neutral['50'],
                        color: colors.palette.neutral['700'],
                    },
                    '&.nmSurveyCard': {
                        '.nmSurveyCardInfoTitle': {
                            color: colors.palette.neutral['800'],
                        },
                        '.nmSurveyCardLabel': {
                            color: colors.palette.neutral['600'],
                        },
                        '.nmSurveyCardInfoIcon svg path': {
                            fill: 'rgba(140, 140, 140, 1)',
                        },
                    },
                    '&.MuiPaper-root.referralIntroWrapper': {
                        padding: '0',
                        background: 'transparent',
                        boxShadow: 'none',
                        border: '0',
                        '.referralIntroContainer': {
                            justifyContent: 'space-between',
                            marginTop: '-12px',
                            padding: '0',
                        },
                        '.referralIntroItem': {
                            display: 'inline-flex',
                            flexDirection: 'column',
                            flexBasis: '100%',
                            maxWidth: '100%',
                            background: 'white',
                            borderRadius: borderRadius.primary,
                            border: `1px solid ${colors.palette.neutral['100']}`,
                            padding: '24px',
                            margin: '12px 0 0',
                            position: 'relative',
                        },
                        '.referralInfoWrapper': {
                            width: '100%',
                            marginTop: '8px',
                        },
                        '.referralIntroIconWrapper': {
                            color: colors.palette.tertiary['500'],
                        },
                        '.referralIntroQuestionNote': {
                            margin: '6px 0 12px 0',
                        },
                        [breakpoints.up('md')]: {
                            '.referralIntroContainer': {
                                marginTop: '-24px',
                            },
                            '.referralIntroItem': {
                                flexBasis: 'calc(33.3% - 15px)',
                                maxWidth: 'calc(33.3% - 15px)',
                                marginTop: '24px',
                            },
                        },
                    },
                    '&.referralsFiltersWrapper': {
                        padding: '16px 24px',
                        boxShadow: 'none',
                        background: colors.palette.secondary['500'],
                        marginBottom: '12px',
                        '.referralReferText': {
                            color: 'white',
                            marginBottom: '2px',
                        },
                        '.referralCopyLink .copyNotification': {
                            color: 'white',
                            background: addOpacityToThemeColor(colors.palette.primary.main, 0.9),
                        },
                    },
                    '&.referralCardWrapper': {
                        padding: '0',
                        '.referralDetails': {
                            background: 'white',
                            paddingBottom: '0',
                            padding: '14px 24px',
                        },
                        '.referralsDetailTitle': {
                            color: colors.palette.primary['800'],
                            fontFamily: fonts.primary,
                            fontSize: '1.25rem',
                            fontWeight: '800',
                        },
                        '.referralDetailContent li': {
                            marginLeft: '1rem',
                            marginTop: '0.25rem',
                        },
                        '.referralDetailSubtitle': {
                            display: 'inline-flex',
                            fontSize: '0.875rem',
                            color: colors.palette.neutral['500'],
                            marginBottom: '0.25rem',
                        },
                        '.referralActionButtonBlock': {
                            padding: '12px 24px 24px 24px',
                        },
                    },
                },
            },
        },
        MuiDivider: defaultTheme.MuiDivider,
        MuiTabs: {
            styleOverrides: {
                flexContainer: {
                    gap: '8px',
                },
                root: {
                    ...defaultTheme.MuiTabs.styleOverrides.root,
                    border: 'none',
                    borderRadius: 0,
                    width: '100%',
                    [breakpoints.up('sm')]: {
                        '&.MuiTabs-root': {
                            width: 'fit-content',
                        },
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    color: colors.palette.text.primary,
                    backgroundColor: colors.palette.neutral['50'],
                    borderRadius: borderRadius.primary,
                    minHeight: '40px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: '400',
                    [breakpoints.up('sm')]: {
                        '&.MuiButtonBase-root': {
                            flexBasis: 'auto',
                            padding: '10px 16px',
                        },
                    },
                    '&:hover': {
                        color: colors.palette.primary['300'],
                        backgroundColor: colors.palette.neutral['50'],
                    },
                    '&.Mui-selected': {
                        ...defaultTheme.MuiTab.styleOverrides.root['&.Mui-selected'],
                        color: 'white',
                        backgroundColor: colors.palette.primary.main,
                        '&:hover': {
                            color: 'white',
                            backgroundColor: colors.palette.primary['400'],
                        },
                    },
                    '&.Mui-disabled': {
                        color: colors.palette.neutral['500'],
                        backgroundColor: colors.palette.neutral['100'],
                    },
                },
            },
        },
        MuiTabPanel: defaultTheme.MuiTabPanel,
        MuiTableHead: defaultTheme.MuiTableHead,
        MuiTableCell: {
            styleOverrides: {
                root: {
                    ...defaultTheme.MuiTableCell.styleOverrides.root,
                    backgroundColor: 'transparent',
                },
                head: {
                    ...defaultTheme.MuiTableCell.styleOverrides.head,
                    color: colors.other.table.headerText,
                    fontWeight: '400',
                    borderBottomColor: colors.palette.neutral['100'],
                },
            },
        },
        MuiTableRow: defaultTheme.MuiTableRow,
        MuiPickersPopper: defaultTheme.MuiPickersPopper,
        MuiPickersDay: defaultTheme.MuiPickersDay,
        MuiSkeleton: defaultTheme.MuiSkeleton,
        MuiPaginationItem: {
            styleOverrides: {
                root: {
                    background: 'none',
                    borderRadius: borderRadius.primary,
                    color: colors.palette.text.primary,
                    '&:hover': {
                        background: 'none',
                        color: colors.palette.primary['300'],
                    },
                    '&:active': {
                        color: 'white',
                        backgroundColor: colors.palette.primary['500'],
                    },
                    '&.Mui-selected': {
                        color: 'white',
                        backgroundColor: colors.palette.primary['500'],
                        '&:hover': {
                            backgroundColor: colors.palette.primary['400'],
                        },
                    },
                    '&.Mui-disabled': {
                        background: 'none',
                        color: colors.palette.neutral['500'],
                        opacity: '1',
                    },
                    '&.MuiPaginationItem-previousNext': {
                        background: 'none',
                        '&:hover': {
                            background: 'none',
                            color: colors.palette.primary['300'],
                        },
                        '&:active': {
                            background: 'none',
                            color: colors.palette.primary['800'],
                        },
                    },
                },
            },
        },
    },
});

export default theme;
